import type { GridApi } from '@repo/go-table';
import { reaction, type IReactionDisposer } from 'mobx';
import type { MutableRefObject } from 'react';

import type { FactTrajectoryPointEntity } from '../../service/entities';
import type { TrajectoryService } from '../../service/trajectory-service';

export class TrajectoryTableStore {
  private readonly tableApi: MutableRefObject<GridApi>;
  private readonly trajectoryService: TrajectoryService;
  private syncedRowIds = new Set<string>();
  private selectionDisposer: IReactionDisposer | null = null;

  constructor(tableApi: MutableRefObject<GridApi>, trajectoryService: TrajectoryService) {
    this.tableApi = tableApi;
    this.trajectoryService = trajectoryService;
  }

  private addPoints = (points: FactTrajectoryPointEntity[]): void => {
    if (!points.length) {
      return;
    }

    this.tableApi.current.updateRows(points);

    const lastPoint = points[points.length - 1];

    if (!lastPoint) {
      return;
    }

    setTimeout(() => {
      this.tableApi.current.setCellFocus(lastPoint.entityId, 'md');
      this.tableApi.current.startCellEditMode({ id: lastPoint.entityId, field: 'md' });
    }, 100);
  };

  private updatePoints = (points: FactTrajectoryPointEntity[]): void => {
    if (!points.length) {
      return;
    }

    this.tableApi.current.updateRows(points);
  };

  private deletePoints = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    const focused = this.tableApi.current.state.focus.cell;

    if (focused && ids.includes(String(focused.id))) {
      const mode = this.tableApi.current.getCellMode(focused.id, focused.field);

      if (mode === 'edit') {
        this.tableApi.current.stopCellEditMode({
          id: focused.id,
          field: focused.field,
          ignoreModifications: true,
        });
      }
    }

    this.tableApi.current.updateRows(ids.map((entityId) => ({ entityId, _action: 'delete' })));
  };

  private syncRowsWithSelectedTrajectory = (): void => {
    const points = this.trajectoryService.mainFactTrajectory?.points ?? [];
    const nextRowIds = new Set(points.map((point) => point.entityId));
    const removedIds = [...this.syncedRowIds].filter((id) => !nextRowIds.has(id));

    if (removedIds.length > 0) {
      this.tableApi.current.updateRows(removedIds.map((entityId) => ({ entityId, _action: 'delete' })));
    }

    if (points.length > 0) {
      this.tableApi.current.updateRows(points);
    }

    this.syncedRowIds = nextRowIds;
  };

  init = (): VoidFunction => {
    const disposeAdd = this.trajectoryService.eventEmitter.addEventListener('newPoints', this.addPoints);
    const disposeUpdate = this.trajectoryService.eventEmitter.addEventListener('updatePoints', this.updatePoints);
    const disposeDelete = this.trajectoryService.eventEmitter.addEventListener('deletePoints', this.deletePoints);
    this.selectionDisposer = reaction(
      () => this.trajectoryService.mainFactTrajectory?.entityId,
      () => {
        this.syncRowsWithSelectedTrajectory();
      },
      { fireImmediately: true },
    );

    return () => {
      disposeAdd();
      disposeUpdate();
      disposeDelete();
      this.selectionDisposer?.();
      this.selectionDisposer = null;
      this.syncedRowIds = new Set();
    };
  };
}
